import { Box, Button, StackProps, VStack } from "@chakra-ui/react";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React, { useState, useMemo } from "react";
import { AiOutlineRead } from "react-icons/ai";
import { getDataSourceInstruction } from "../../../../helper/dataSource/getDataSourceInstructions";
import { useCurrentBreakpoint } from "../../../../hooks/useCurrentBreakpoint";
import {
  dataSourceInstructionMarkdocConfig,
  dataSourceInstructionComponents,
} from "../../../../markdoc/markdocConfig";
import { MarkdocContent } from "../../../../markdoc/MarkdocContent";
import { ImportMethod } from "../panels/ImportMethodSelectionPanel";

/**
 * Data source instructions section.
 * When there is enough viewport space, it will display in a modal,
 * otherwise it will display inline.
 *
 * There must be a 'position'ed parent (e.g. position:relative) somewhere in
 * the tree, otherwise the modal will not be positioned correctly.
 */
export function InstructionsSection({
  dataSource,
  importMethod,
  isNewDataSource,
  ...props
}: {
  dataSource: DataSourceWithNetworks;
  importMethod: ImportMethod;
  isNewDataSource: boolean;
} & StackProps): React.ReactElement | null {
  const [showInstructions, setShowInstructions] = useState<boolean>();

  // decide if there is enough space for the instructions to display in a modal.
  const { isAtLeast } = useCurrentBreakpoint();
  const hasSpaceForModal = isAtLeast("lg");

  // if the user hasn't toggled the instructions to show/ hide, then we will
  // show the instructions if it's a large device with space available,
  // otherwise, we hide them by default.
  const isInstructionsVisible = showInstructions ?? hasSpaceForModal;

  const instructionContent = useMemo(
    () =>
      getDataSourceInstruction({
        dataSource,
        importMethod,
        isNewDataSource,
      }),
    [dataSource, importMethod, isNewDataSource],
  );

  if (hasSpaceForModal) {
    return (
      <Box
        w="full"
        {...{
          position: "absolute",
          right: "calc(100% + 1rem)",
          top: "1rem",
          bottom: "1rem",
          width: "30rem",
          maxWidth: "30rem",
          backgroundColor: "white",
          borderRadius: "xl",
          borderColor: "gray.300",
          borderWidth: "1px",
          shadow: "lg",
          overflowY: "auto",
          px: 6,
          py: 8,
        }}
      >
        <MarkdocContent
          config={dataSourceInstructionMarkdocConfig}
          content={instructionContent.content}
          variables={instructionContent.variables}
          components={dataSourceInstructionComponents}
          as="article"
          w="full"
          flex={1}
        />
      </Box>
    );
  }

  return (
    <VStack spacing={0} gap={4} align="flex-start" {...props}>
      <Button
        leftIcon={<AiOutlineRead />}
        variant="outline"
        size="sm"
        colorScheme="gray"
        onClick={() => setShowInstructions(!isInstructionsVisible)}
      >
        {isInstructionsVisible ? "Hide instructions" : "View instructions"}
      </Button>

      {isInstructionsVisible && (
        <MarkdocContent
          config={dataSourceInstructionMarkdocConfig}
          content={instructionContent.content}
          variables={instructionContent.variables}
          components={dataSourceInstructionComponents}
          as="article"
          w="full"
          flex={1}
        />
      )}
    </VStack>
  );
}
